import React, { useState, useEffect } from 'react';
import { Heart, Camera, MapPin, Phone, Mail, Instagram, Facebook, Twitter, Youtube, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useTheme } from '../context/ThemeContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { themeData } = useTheme();
  const [companyInfo, setCompanyInfo] = useState({
    companyName: 'Gen-X Photo Studio JA',
    businessAddress: 'Morant Bay, Jamaica',
    phoneNumber: '1 (876) 805-1013',
    emailAddress: 'genxphotostudioja@gmail.com',
  });

  useEffect(() => {
    fetch('https://ops.genxphotostudioja.com/api/public/company-info')
      .then(res => res.json())
      .then(data => setCompanyInfo(data))
      .catch(err => console.error('Error loading company info:', err));
  }, []);

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Specials', href: '#specials' },
    { name: 'Valentine Gifts', href: '#valentine' },
    { name: 'Categories', href: '#categories' },
    { name: 'Customize', href: '#customize' },
    { name: 'Locations', href: '#locations' },
  ];

  const services = [
    'Portrait Photography',
    'Event Coverage',
    'Custom Photo Gifts',
    'Canvas Printing',
    'Photo Restoration',
    'Corporate Services',
  ];

  const policyLinks = [
    { name: 'Refund Policy', href: '/refund-policy.html' },
    { name: 'Privacy Policy', href: '/privacy-policy.html' },
    { name: 'Terms of Service', href: '/terms-of-service.html' },
    { name: 'Delivery Policy', href: '/delivery-policy.html' },
  ];

  const socialLinks = [
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Youtube, href: '#', label: 'YouTube' },
  ];

  return (
    <footer className="bg-[#0d0d1a] border-t border-white/5">
      {/* Newsletter Section */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">Get 10% Off Your First Order</h3>
              <p className="text-white/60">Subscribe to our newsletter for exclusive deals and updates</p>
            </div>
            <div className="flex gap-3 w-full lg:w-auto">
              <Input 
                type="email" 
                placeholder="Enter your email" 
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 min-w-[250px]"
              />
              <Button 
                className="whitespace-nowrap text-white"
                style={{ backgroundColor: themeData.colors.primary }}
              >
                Subscribe
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="/genx-logo.png" 
                alt="Gen-X Photo Studio" 
                className="h-10 w-auto"
                style={{ filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.3))' }}
              />
              <span className="text-white font-bold text-lg">Gen-X Photo Studio</span>
            </div>
            <p className="text-white/60 text-sm mb-6 leading-relaxed">
              Capturing life's precious moments with Caribbean warmth and professional excellence since 2015.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/60 hover:text-white transition-all"
                  style={{ 
                    '--hover-bg': themeData.colors.primary,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = themeData.colors.primary}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.href} className="text-white/60 hover:text-white transition-colors text-sm flex items-center gap-2 group">
                    <ArrowRight 
                      className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all"
                      style={{ color: themeData.colors.primary }}
                    />
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-white/60 text-sm flex items-center gap-2">
                    <Camera className="w-4 h-4" style={{ color: themeData.colors.secondary }} />
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-bold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: themeData.colors.primary }} />
                <span className="text-white/60 text-sm">{companyInfo.businessAddress}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 flex-shrink-0" style={{ color: themeData.colors.secondary }} />
                <span className="text-white/60 text-sm">{companyInfo.phoneNumber}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 flex-shrink-0" style={{ color: themeData.colors.accent }} />
                <span className="text-white/60 text-sm">{companyInfo.emailAddress}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm text-center sm:text-left">
              © {currentYear} {companyInfo.companyName}. All rights reserved. Made with <Heart className="w-4 h-4 inline fill-current" style={{ color: themeData.colors.primary }} /> in Jamaica
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              {policyLinks.map((link) => (
                <a key={link.name} href={link.href} className="text-white/40 hover:text-white/60 text-sm transition-colors">{link.name}</a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
